import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginFullSync } from '../services/syncService';
import dataService from '../services/dataService';

const SYNC_AFTER_LOGIN_KEY = 'btmx.syncAfterLogin';

/** Vom Login aufgerufen: Die App öffnet sofort, der Abgleich startet danach im Hintergrund. */
export function requestSyncAfterLogin() {
  try { sessionStorage.setItem(SYNC_AFTER_LOGIN_KEY, '1'); } catch { /* ignore */ }
}

function takeSyncAfterLoginRequest() {
  try {
    if (sessionStorage.getItem(SYNC_AFTER_LOGIN_KEY) !== '1') return false;
    sessionStorage.removeItem(SYNC_AFTER_LOGIN_KEY);
    return true;
  } catch {
    return false;
  }
}

const SyncContext = createContext(null);

export function SyncProvider({ children }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState({ percent: 0, message: '' });

  const refreshLastSync = useCallback(async () => {
    try {
      const meta = await dataService.getSyncMetadata();
      setLastSync(meta.lastSync ? new Date(meta.lastSync) : null);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshLastSync();
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    const onSync = () => refreshLastSync();
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('sync-completed', onSync);
    const tick = setInterval(refreshLastSync, 60_000);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('sync-completed', onSync);
      clearInterval(tick);
    };
  }, [refreshLastSync]);

  const triggerSync = useCallback(async ({ manual = false } = {}) => {
    if (syncing || !navigator.onLine) {
      return { success: false, reason: syncing ? 'already_running' : 'offline' };
    }
    setSyncing(true);
    setProgress({ percent: 0, message: 'Verbindung wird hergestellt …' });
    try {
      const result = await loginFullSync((percent, message) => {
        if (percent >= 0) setProgress({ percent, message });
      }, { manual });
      if (result?.success) await refreshLastSync();
      return result;
    } catch (e) {
      return { success: false, error: e?.message || 'Synchronisation fehlgeschlagen.' };
    } finally {
      setSyncing(false);
      setProgress({ percent: 100, message: '' });
    }
  }, [syncing, refreshLastSync]);

  useEffect(() => {
    if (takeSyncAfterLoginRequest()) triggerSync();
    // nur beim ersten Einhängen nach dem Login
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ageHours = lastSync ? (Date.now() - lastSync.getTime()) / 3.6e6 : null;
  let state = 'fresh';
  if (syncing) state = 'syncing';
  else if (!online) state = 'offline';
  else if (ageHours == null) state = 'never';
  else if (ageHours > 48) state = 'stale';

  return (
    <SyncContext.Provider value={{
      online,
      lastSync,
      syncing,
      progress,
      state,
      triggerSync,
    }}>
      {children}
    </SyncContext.Provider>
  );
}

export function useSync() {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within SyncProvider');
  return ctx;
}
